import React, {useState, useEffect} from "react";
import "./Paginator.component.scss";

export interface PaginatorProps {
    currentPage: number;
    totalPages: number;
    onPageChange: (newPage: number) => void;
}

export default function Paginator(props: PaginatorProps) {
    const {currentPage, handleChangePage, setCurrentPage} = useChangePage(
        props.currentPage,
        props.onPageChange
    );

    const [inputPage, setInputPage] = useState(props.currentPage.toString());

    const disabledBack = currentPage <= 1;
    const disabledForward = currentPage >= props.totalPages;

    useEffect(() => {
        setCurrentPage(props.currentPage);
        setInputPage(props.currentPage.toString());
    }, [props.currentPage]);

    useEffect(() => {
        if (currentPage > props.totalPages) {
            setCurrentPage(props.totalPages);
        }
    }, [props.totalPages]);

    const goToInputPage = () => {
        const parsed = parseInt(inputPage, 10);
        if (!isNaN(parsed) && parsed >= 1 && parsed <= props.totalPages) {
            if (parsed !== currentPage) {
                handleChangePage(parsed - currentPage);
            }
            setInputPage(parsed.toString()); // normalize after change
        } else {
            setInputPage(currentPage.toString()); // reset invalid
        }
    };

    return (
        <div id="Paginator">
            <div
                className={`paginator-first ${disabledBack ? "disabled" : ""}`}
                onClick={() => !disabledBack && handleChangePage(1 - currentPage)}
                title="First page"
            >
                <i className="material-icons">first_page</i>
            </div>

            <div
                className={`paginator-back ${disabledBack ? "disabled" : ""}`}
                onClick={() => !disabledBack && handleChangePage(-1)}
                title="Previous page"
            >
                <i className="material-icons">keyboard_arrow_left</i>
            </div>

            <div className="paginator-current-page">
                <input
                    className="page-input"
                    type="number"
                    min={1}
                    max={props.totalPages}
                    value={inputPage}
                    onChange={(e) => setInputPage(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && goToInputPage()}
                    onBlur={() => {
                        if (parseInt(inputPage) !== currentPage) {
                            goToInputPage();
                        }
                    }}
                />
                <span className="separator">/</span>
                <span className="total-pages">{props.totalPages}</span>
            </div>

            <div
                className={`paginator-next ${disabledForward ? "disabled" : ""}`}
                onClick={() => !disabledForward && handleChangePage(1)}
                title="Next page"
            >
                <i className="material-icons">keyboard_arrow_right</i>
            </div>

            <div
                className={`paginator-last ${disabledForward ? "disabled" : ""}`}
                onClick={() => !disabledForward && handleChangePage(props.totalPages - currentPage)}
                title="Last page"
            >
                <i className="material-icons">last_page</i>
            </div>
        </div>
    );
}

const useChangePage = (
    initialValue: number,
    callback: (newPage: number) => void
) => {
    const [currentPage, setCurrentPage] = useState(initialValue);

    const handleChangePage = (delta: number) => {
        const newPage = currentPage + delta;
        if (newPage >= 1 && Number.isInteger(newPage)) {
            callback(newPage);
            setCurrentPage(newPage);
        }
    };

    return {
        currentPage,
        handleChangePage,
        setCurrentPage
    };
};
